<template>
  <div class="content content_in">
    <div class="warn-block my-3" v-if="showWarnBlock">
      <svg class="message-icon mr-1" v-svg style="fill: #b84e4e" symbol="info" size="0 0 17 17" role="info"></svg>
      <span v-html="$t('domain.google_warn', { name: bannedDomainIds })"></span>
      <svg v-svg symbol="close-red" @click="showWarnBlock = false" size="0 0 20 20" class="close" role="info"></svg>
    </div>
    <div class="title-row d-flex row justify-content-between">
      <div class="col-12 d-flex w-100 col-md-auto">
        <h1 class="w-100">{{ $t("domain.title") }}</h1>
        <button @click.prevent="$modal.show('buyDomain')" class="btn">
          {{ $t("domain.buy_domain") }}
        </button>
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col-12">
        <ul class="tabs justify-content-start mb-0">
          <li v-for="(tab, index) in tabs" :key="index" class="col-sm-12 col-lg-6">
            <button @click.prevent="currentTab = tab" :class="{ active: currentTab === tab }">
              <svg v-svg :symbol="tab === 'archive' ? 'delete' : 'active'" size="0 0 15 15" role="info"></svg>
              {{ $t(`domain.tabs.${tab}`) }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="c-table">
      <div class="inner">
        <vue-good-table styleClass="table  _no-wrap" :columns="visibleColumns" :rows="rows">
          <template slot="table-column" slot-scope="props">
            <span @click="toSort(props.column.field)" class="cursor-pointer">
              {{ formatLabel(props.column.label) }}
            </span>
            <template v-if="props.column.field === 'limit'">
              <svg
                v-svg
                v-tooltip="{
                  content: $t('domain.popup_limit'),
                  triggers: ['hover', 'click'],
                }"
                class="mr-1"
                symbol="info"
                size="0 0 18 18"
                role="info"
                style="fill: #4d78c8"
              ></svg>
            </template>
          </template>

          <template slot="table-row" slot-scope="props">
            <template v-if="props.column.field === 'status'">
              <span class="domain-field">
                <small v-if="props.row.is_banned_by_gsb" class="locked-by-google">
                  <svg v-svg symbol="info" size="0 0 15 15" role="info"></svg>
                  Google Safe Browsing
                </small>
                <span class="active" v-else>
                  <svg v-svg color="red" symbol="active" size="0 0 15 16"></svg>
                  Active</span
                >
              </span>
            </template>

            <template v-else-if="props.column.field === 'type'">
              {{ props.row.category ? props.row.category : "-" }}
            </template>

            <template v-else-if="props.column.field === 'total_subscriptions'">
              {{ props.row.total_subscriptions ? props.row.total_subscriptions : "-" }}
            </template>

            <template v-else-if="props.column.field === 'limit' && props.row.category === 'Landing'">
              <div class="d-flex" v-if="featureFlags && featureFlags.includes('domain_subscription_limit')">
                <small v-if="props.row.current_subscriptions == null">
                  <svg
                    class="mr-2"
                    v-tooltip="{
                      content: $t('domain.table.limit_error'),
                      triggers: ['click', 'hover'],
                    }"
                    v-svg
                    style="opacity: 0.8; margin-top: 14px"
                    size="0 0 20 20"
                    symbol="error"
                  ></svg>
                </small>
                <small class="inf" v-else>
                  {{ !props.row.subscription_limited ? "∞" : props.row.current_subscriptions }}
                </small>

                <small class="max" v-if="props.row.subscription_limited && props.row.current_subscriptions >= props.row.subscription_limit && props.row.subscription_limit !== null && props.row.current_subscriptions !== 0">
                  <svg
                    class="mb-1"
                    v-tooltip="{
                      content: $t('domain.table.limit_exceeded'),
                      triggers: ['click', 'hover'],
                    }"
                    v-svg
                    size="0 0 20 20"
                    symbol="error"
                  ></svg>
                  {{ props.row.current_subscriptions }}</small
                >
                <div class="limit-interval d-flex">
                  <Counter
                    v-model="props.row.subscription_limit"
                    class="p-0 w-25 mr-2"
                    :infinity="!props.row.subscription_limited"
                    :disabled="!props.row.subscription_limited || currentTab === 'archive'"
                    :max="1000000"
                    :min="500"
                    :placeholder="!props.row.subscription_limited ? '∞' : ''"
                    :invalid="props.row.subscription_limit === props.row.current_subscriptions && props.row.current_subscriptions !== null"
                    :noSpace="true"
                    @input="debouncedUpdateLimitAndInterval(props.row)"
                  />

                  <custom-multiselect v-model="props.row.subscription_interval" class="multiselect-wrapper ml-4 width-125" :list="timeOptions" :disabled="!props.row.subscription_limited" :infinite="!props.row.subscription_limited" :allowEmpty="false" @input="updateLimitAndInterval(props.row)">
                    <template v-slot:block="props">
                      <span>
                        {{ props.value.title }}
                      </span>
                    </template>
                    <template v-slot:list="props">
                      <span>
                        {{ props.value.title }}
                      </span>
                    </template>
                  </custom-multiselect>

                  <CheckBoxWrapper class="p-0 ml-2 mt-3" :value="!props.row.subscription_limited" @input="(val) => updateLimitAndInterval(props.row, val)" :text="$t('domain.unlimited')" />
                </div>
              </div>
            </template>

            <template v-else-if="props.column.field === 'control'">
              <ul class="controls">
                <li v-if="isActive && props.row.category === 'Landing'">
                  <button
                    class="bg-transparent"
                    @click.prevent="
                      $modal.show('domainSettings');
                      openSettings(props.row.id);
                    "
                  >
                    <svg v-svg symbol="settings" style="fill: #505050" size="0 0 14 14" role="info"></svg>
                  </button>
                </li>

                <li>
                  <button @click="trashDomain(props.row.id)" class="controls__btn" :class="{ trash: !isActive }" :title="$t('select.deselectLabel')">
                    <svg v-if="!isActive" width="6" height="12" viewBox="0 0 6 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.48457 0.307119L0.199266 5.45137C-0.0664225 5.7686 -0.0664225 6.2314 0.199266 6.54863L4.48457 11.6929C4.79311 12.053 5.33309 12.1045 5.69306 11.8044C6.05302 11.4958 6.10445 10.9557 5.80448 10.5956L1.97344 6L5.80448 1.40437C6.10445 1.04428 6.05302 0.504206 5.69305 0.195551C5.33309 -0.104531 4.79311 -0.0529787 4.48457 0.307119Z" />
                    </svg>

                    <svg v-svg symbol="delete" size="0 0 15 15" role="info"></svg>
                  </button>
                </li>
              </ul>
            </template>
          </template>
          <div class="d-flex justify-content-center" slot="emptystate">
            <template v-if="!loadDataTable">
              {{ $t("any_results") }}
            </template>
            <template v-else>
              <div class="d-flex justify-content-center">
                <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
              </div>
            </template>
          </div>
        </vue-good-table>
      </div>
    </div>

    <div class="c-pagination">
      <ul class="pagination" v-if="rows && rows.length && rows.length > 0">
        <li class="page-item">
          <button @click.prevent="page--" :class="{ disabled: page <= 1 }" :disabled="page <= 1" class="page-link icon-prev prev" data-ci-pagination-page="2" rel="prev" :title="$t('statistic.prev')">
            <svg v-svg symbol="prev" size="0 0 8 14"></svg>
          </button>
        </li>
        <li class="page-item">
          <button @click.prevent="page++" :class="{ disabled: page * limit >= total }" :disabled="page * limit >= total" class="page-link icon-next next" data-ci-pagination-page="2" rel="next" :title="$t('statistic.next')">
            <svg v-svg symbol="next" size="0 0 8 14"></svg>
          </button>
        </li>
        <li class="page-item">
          {{ page === 1 ? page : limit * (page - 1) + 1 }} - {{ limit * (page - 1) + rows.length }}
          {{ $t("from") }}
          {{ total }}
        </li>
        <li>
          <custom-multiselect class="ml-3" v-model="limit" :list="optionsPagination" :allowEmpty="false">
            <template v-slot:block="props">
              <span>
                {{ props.value }}
              </span>
            </template>
            <template v-slot:list="props">
              <span>
                {{ props.value }}
              </span>
            </template>
          </custom-multiselect>
        </li>
      </ul>
    </div>

    <modal classes="creative-modal" name="buyDomain" height="auto" :width="570" :scrollable="true" :shiftY="0" @closed="$modal.hide('buyDomain')">
      <BuyDomain @closeModal="$modal.hide('buyDomain')" @buy-domain="buyDomain" @refresh="refreshDomain" @showError="showError" />
    </modal>

    <modal classes="creative-modal" :width="570" height="auto" name="confirmDomain" :scrollable="true" :shiftY="0" @closed="$modal.hide('confirmDomain')">
      <ConfirmDomain :domain-type="domainType" :current-domain="currentDomain" @refresh="refreshDomain" @success="onSuccess" @closeModal="$modal.hide('confirmDomain')" />
    </modal>

    <modal classes="creative-modal" name="successBuyDomain" :width="570" height="auto" :scrollable="true" :shiftY="0" @closed="$modal.hide('successBuyDomain')">
      <SuccessBuyDomain @closeModal="$modal.hide('successBuyDomain')" />
    </modal>

    <modal @closed="$modal.hide('domainSettings')" classes="creative-modal" :width="570" name="domainSettings" height="auto" :scrollable="false" :shiftY="0">
      <DomainSettings :domain="activeDomainSettings" @closeModal="$modal.hide('domainSettings')" />
    </modal>

    <alert v-if="$alert.title" />
  </div>
</template>
<script>
import "@/assets/css/_pagination.scss";
import serviceDomain from "@/api/serviceDomain";
import BuyDomain from "@/components/BuyDomain.vue";
import ConfirmDomain from "@/components/ConfirmDomain.vue";
import SuccessBuyDomain from "@/components/SuccessBuyDomain.vue";
import debounce from "@/mixins/debounce";
import Counter from "@/components/PlatformSettings/Counter.vue";
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import DomainSettings from "@/components/DomainSettings.vue";
import { mapGetters } from "vuex";

export default {
  name: "Domain",
  components: {
    BuyDomain,
    ConfirmDomain,
    SuccessBuyDomain,
    Counter,
    CheckBoxWrapper,
    DomainSettings,
  },
  mixins: [debounce],
  data() {
    return {
      activeDomainSettings: null,
      bannedDomainIds: null,
      showWarnBlock: false,
      currentTab: "active",
      tabs: ["active", "archive"],
      rows: [],
      columns: [
        {
          label: "platforms.table.id",
          field: "id",
          sortable: false,
        },
        {
          label: "platforms.domain",
          field: "domain",
          sortable: false,
        },
        {
          label: "platforms.table.type",
          field: "type",
          sortable: false,
        },
        {
          label: "platforms.table.status",
          field: "status",
          sortable: false,
        },
        {
          label: "domain.table.buy_date",
          field: "buy_date",
          thClass: `sortable spec-sort`,
          sortable: true,
        },
        {
          label: "domain.table.expires_at",
          field: "expires_at",
          sortable: false,
        },

        {
          label: "",
          field: "control",
          width: "35%",
          sortable: false,
        },
      ],
      page: 1,
      offset: null,
      limit: 10,
      total: null,
      order: null,
      sort: null,
      loadDataTable: false,
      optionsPagination: [10, 25, 50, 100],
      domainType: null,
      currentDomain: null,
    };
  },
  computed: {
    ...mapGetters({
      featureFlags: "user/getFeatureFlags",
    }),
    timeOptions() {
      return [
        {
          title: "1" + this.$t("hours"),
          value: 3600,
        },
        {
          title: "2" + this.$t("hours"),
          value: 7200,
        },
        {
          title: "3" + this.$t("hours"),
          value: 10800,
        },
        {
          title: "6" + this.$t("hours"),
          value: 21600,
        },
        {
          title: "12" + this.$t("hours"),
          value: 43200,
        },
        {
          title: "24" + this.$t("hours"),
          value: 86400,
        },
        {
          title: this.$t("domain.all_time"),
          value: 0,
        },
      ];
    },
    isActive() {
      return this.currentTab === "active";
    },
    visibleColumns() {
      const hiddenFields = this.currentTab === "archive" ? ["status", "limit"] : [];

      return this.columns.filter((col) => !hiddenFields.includes(col.field));
    },
  },
  methods: {
    openSettings(id) {
      this.activeDomainSettings = id;
    },
    buyDomain(type, domain) {
      this.domainType = type;
      this.currentDomain = domain;

      this.$modal.hide("buyDomain");
      this.$modal.show("confirmDomain");
    },
    refreshDomain() {
      this.loadDataTable = true;
      let params = this.$route.query;
      let payload = {
        trash: +!this.isActive,
        offset: params.offset ? params.offset : this.offset,
        limit: this.limit,
        order: this.order,
        sort: this.sort,
      };
      serviceDomain
        .getDomain(payload)
        .then((resp) => {
          if (resp && resp.status === 200) {
            this.rows = resp.data.domains.map((row) => {
              return {
                ...row,
                subscription_limit: row.subscription_limit || 500,
                subscription_interval: this.timeOptions.find((el) => el.value === row.subscription_interval) || {
                  title: "1h",
                  value: 3600,
                },
                _initialized: false,
              };
            });

            this.$nextTick(() => {
              this.rows.forEach((row) => (row._initialized = true));
            });

            this.total = resp.data.total;

            if (!payload.trash) {
              // Get the list of banned domain IDs as a comma-separated string
              this.bannedDomainIds = this.rows
                .filter((row) => row.is_banned_by_gsb)
                .map((row) => row.domain)
                .toString();

              const gspBlocked = localStorage.getItem("blocked-by-gsp") || "";
              const isBlockedInStorage = gspBlocked.includes(this.bannedDomainIds);
              const isBlockedTrue = gspBlocked.includes("true");
              const isBlockedFalse = gspBlocked.includes("false");

              const blockedIds = gspBlocked.match(/\d+/g) ? gspBlocked.match(/\d+/g).map(Number).toString() : "";

              // Condition to show warning block
              if ((isBlockedInStorage && isBlockedTrue) || (this.bannedDomainIds && (!gspBlocked || isBlockedTrue)) || (this.bannedDomainIds !== blockedIds && isBlockedFalse)) {
                this.showWarnBlock = true;
              } else {
                this.showWarnBlock = false;
              }

              localStorage.setItem("blocked-by-gsp", `${this.bannedDomainIds}${this.showWarnBlock}`);
            }
          }
        })
        .finally(() => {
          this.loadDataTable = false;
        });
    },
    onSuccess() {
      this.$modal.hide("confirmDomain");
      this.$modal.show("successBuyDomain");
    },
    updateLimitAndInterval(row, switcherVal) {
      if (!row._initialized) return;
      const id = row.id;
      if (typeof switcherVal === "boolean") {
        row.subscription_limited = !switcherVal;
      }
      const payload = {
        subscription_limited: row.subscription_limited,
        subscription_limit: row.subscription_limited ? row.subscription_limit ?? 500 : 500,
        subscription_interval: row.subscription_limited ? row.subscription_interval?.value : null,
      };
      // Compare with last payload to prevent duplicate requests
      const lastPayload = row._lastLimitPayload;
      if (lastPayload && lastPayload.subscription_limited === payload.subscription_limited && lastPayload.subscription_limit === payload.subscription_limit && lastPayload.subscription_interval === payload.subscription_interval) {
        return;
      }
      row._lastLimitPayload = { ...payload };

      serviceDomain.setLimitIntervalSubs(id, payload).then((res) => {
        if (res && res.status === 200) {
          this.$alert({
            title: res.message,
            type: "success",
          });
          this.refreshDomain();
        } else {
          this.$alert({
            title: this.$t("sent_error"),
            message: res,
          });
        }
      });
    },
    trashDomain(id) {
      const payload = {
        trash: this.isActive,
      };

      serviceDomain.trashDomain(id, payload).then((res) => {
        if (res && res.status === 200) {
          this.$alert({
            title: this.$t("domain.remove_domain_success"),
            type: "success",
          });

          this.refreshDomain();
        } else {
          this.$alert({
            title: this.$t("sent_error"),
            message: res?.message,
            type: "error",
          });
        }
      });
    },
    checkTableSpan() {
      const thElements = document.querySelectorAll("th.sortable"); // all sortable table columns
      thElements.forEach((thElement) => {
        const spanElement = thElement.querySelector("span"); // span of an current iteration th
        if (spanElement && spanElement.innerText.trim() === this.$t(this.columns.find((el) => el.field === this.sort).label).trim()) {
          // check if an span text === current sort value text
          thElement.classList.remove(
            // remove oposite class
            this.order && this.order === "desc" ? "sorting-asc" : "sorting-desc"
          );
          thElement.classList.add(
            // add class to the current sort th
            this.order && this.order === "desc" ? "sorting-desc" : "sorting-asc"
          );
          return false;
        } else {
          // if no remove all classes
          thElement.classList.remove("sorting-desc", "sorting-asc");
        }
      });
    },
    formatLabel(el) {
      return this.$t(el);
    },
    toSort(val) {
      if (val === "buy_date") {
        this.order = this.sort === val ? (this.order === "asc" ? "desc" : "asc") : "desc";
        this.sort = val;
      }
    },
    showError(message) {
      this.$alert({
        title: message,
      });
    },
  },
  watch: {
    currentTab() {
      this.refreshDomain();
      this.page = 1;
      this.offset = null;
      this.rows = [];
      this.$router
        .push({
          path: "/domain",
        })
        .catch(() => {});
    },
    $route: function () {
      const params = this.$route.query;
      this.offset = params.offset || 0;
      if (this.offset === 0) {
        this.page = 1;
      } else {
        this.page = this.offset / this.limit + 1;
      }
    },
    sort: function () {
      this.checkTableSpan();
    },
    order: function () {
      this.refreshDomain();
      this.checkTableSpan();
    },
    trash: function () {
      this.refreshDomain();
    },
    limit: function () {
      this.page = 1;
      localStorage.setItem("paginationLimit", this.limit);
      this.refreshDomain();
    },
    offset: function () {
      this.refreshDomain();
    },
    page: function () {
      if (this.page === 1 || this.page < 0) {
        this.offset = null;
        this.$router
          .push({
            path: "/domain",
          })
          .catch(() => {});
      } else {
        this.offset = this.limit * (this.page - 1);
        this.$router
          .push({
            path: "/domain",
            query: { offset: this.offset },
          })
          .catch(() => {});
      }
    },
    bannedDomainIds(val) {
      if (val === "") {
        this.showWarnBlock = false;
        localStorage.setItem("blocked-by-gsp", "");
      }
    },
    showWarnBlock(val) {
      if (val === false) localStorage.setItem("blocked-by-gsp", this.bannedDomainIds + "false");
    },
  },
  created() {
    if (this.featureFlags && this.featureFlags.includes("domain_subscription_limit")) {
      this.columns.splice(5, 0, {
        label: "platforms.table.total_subscriptions",
        field: "total_subscriptions",
        sortable: false,
      });

      this.columns.splice(6, 0, {
        label: "domain.table.limit_interval",
        field: "limit",
        sortable: false,
      });
    }
    this.debouncedUpdateLimitAndInterval = this.debounce(this.updateLimitAndInterval, 1000);
  },
  mounted() {
    if (localStorage.getItem("paginationLimit")) {
      this.limit = Number(localStorage.getItem("paginationLimit"));
    }
    this.refreshDomain();
  },
};
</script>

<style lang="scss" scoped>
.width-125 {
  width: 125px;
}

svg.message-icon {
  transform: rotate(180deg);
}

small.inf,
small.max {
  margin-right: 20px;
  margin-top: 12px;
  font-size: 16px;
  display: block;
  color: #909090;
}
small.max {
  color: #c62424;
}
.limit-interval {
  position: relative;

  span.info {
    position: absolute;
    left: 12px;
    top: 10px;
    font-size: 20px;
    z-index: 1;
    background: #f4f4f4;
    color: #909090;
  }
}

.warn-block {
  position: relative;
  background: #fff5f5;
  padding: 20px 30px 20px 20px;
  border-radius: 10px;
  color: #b84e4e;
  span {
    color: #b84e4e !important;
  }
  svg.close {
    position: absolute;
    right: 10px;
    top: 10px;
  }
}

span.domain-field {
  width: 33%;
  display: inline-block;
  span.active {
    color: #37807d;
    svg {
      fill: #37807d;
    }
  }
}
small.locked-by-google,
span.active {
  color: #c62424;
  font-size: 14px;
  font-weight: 400;
  font-family: "Roboto", Arial, Helvetica, sans-serif;
  svg {
    margin-bottom: 2px;
    fill: #c62424;
  }
}

.content.content_in {
  padding-bottom: 150px;

  &::v-deep {
    table thead th.sortable {
      cursor: pointer;
    }
  }

  .controls__btn {
    display: flex;
    align-items: center;
    gap: 0 8px;
    width: 36px;

    &.trash {
      svg {
        fill: #38807d;
      }
    }

    &:hover {
      svg {
        fill: #fff;
      }
    }
  }
}
</style>
